import React, { useEffect, forwardRef, useRef, useState, useContext, useCallback } from 'react';
import { Share } from '@capacitor/share';
import { IonToast, isPlatform } from '@ionic/react';
import { PageContext } from '../../../contexts/AppContexts';
import { getReferalFoodFightLink } from '../../../global/request/user';
import { StartFightContext, ACTIONS } from '../StartFightContext';
import ChallengerModal from './ChallengerModals/ChallengerModal';
import LinkSharedModal from './ChallengerModals/LinkSharedModal';
import '../../../styles/fight/summary/ReferFriendModalSummary.scss';

/**
 * Modal for the "Post to Public" functionality.
 * @param {Object} props The component props
 * @param {number} props.footerHeight The height of the footer
 * @param {Function} props.onClose Function to close the modal
 * @param {React.Ref} ref Reference to the modal element
 * @returns {JSX.Element} The MatchMakingModal component
 */
const ReferFriendModalSummary = forwardRef(({ footerHeight, onClose, bearerToken, foodfightId }, ref) => {
  const referralLink = useRef({ link: '', isFetching: false });
  const startFightCtx = useContext(StartFightContext);
  const pageCtx = useContext(PageContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Referal link copied to clipboard');
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('Link Sent!');

  useEffect(() => {
    if (ref?.current && ref.current.style.bottom !== '0px') {
      ref.current.style.bottom = `${footerHeight}px`;
    }
  }, [ref, footerHeight]);

  const setReferralLink = useCallback(() => {
    if (!foodfightId) {
      console.warn('No foodfightId yet, skipping referral link fetch');
      return;
    }
    referralLink.current.isFetching = true;
    console.log('Fetching referral link for foodfightId:', foodfightId);
    getReferalFoodFightLink(bearerToken, foodfightId)
      .then(result => {
        referralLink.current = { link: result.share_url, isFetching: false };
      })
      .catch(error => {
        referralLink.current.isFetching = false;
        console.error('Failed to fetch referral link:', error);
      });
  }, [bearerToken, foodfightId]);

  useEffect(() => {
    if (bearerToken && foodfightId) {
      setReferralLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearerToken, foodfightId]);

  const copyReferralToClipboard = useCallback(() => {
    try {
      if (referralLink.current.link === '') {
        setToastMessage('Failed to retrieve referral link');
        setShowToast(true);
        return;
      }
      navigator.clipboard
        .writeText(referralLink.current.link)
        .then(() => {
          setIsResultModalOpen(true);
          setModalMsg('Copied Link!');
          startFightCtx.dispatch({
            action: ACTIONS.SET_REFERRAL_LINK_COPIED,
            payload: true,
          });
        })
        .catch(error => {
          console.error('Failed to copy referral code to clipboard after fetch:', error);
          // showReferralLink();
        });
    } catch (error) {
      console.error('Failed to use navigator.clipboard', error);
      // showReferralLink();
    }
    // Refresh referral link. Unsure if this is necessary, but the old implemenation
    // got a new link every time the button was pressed, so this mimics that behavior
    setReferralLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setReferralLink]);

  // Try showing capacitor Share.share modal. Return false on failure
  const tryShareModal = useCallback(async () => {
    try {
      const url = referralLink.current.link;
      const canShareResult = await Share.canShare();
      if (canShareResult.value) {
        try {
          await Share.share({ url });
          setReferralLink();
          setIsResultModalOpen(true);
          setModalMsg('Link Sent!');
          startFightCtx.dispatch({
            action: ACTIONS.SET_REFERRAL_LINK_COPIED,
            payload: true,
          });
        } catch (error) {
          // User closing the modal is considered an error,
          // so the next line checks if this error occurred to not treat it like an error
          if (!error.message.toLowerCase().includes('cancel')) {
            throw new Error(`Failed to use share modal: ${error}`);
          }
        }
      } else {
        throw new Error('canShare returned false');
      }
    } catch (error) {
      throw new Error(`Failed to check canShare: ${error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setReferralLink]);

  // If not on a desktop, will try showing a share modal. Otherwise, copy to clipboard directly
  const handleReferralClick = useCallback(() => {
    if (referralLink.current.isFetching) {
      setToastMessage('Referral link still loading. Try again');
      setShowToast(true);
      return;
    }
    if (isPlatform('desktop')) {
      copyReferralToClipboard();
    } else {
      tryShareModal().catch(error => {
        console.error('Failed to use Share API:', error.message);
        copyReferralToClipboard();
      });
    }
  }, [copyReferralToClipboard, tryShareModal]);

  useEffect(() => {
    startFightCtx.dispatch({
      action: ACTIONS.SET_ON_SHARE_LINK_CLICKED,
      payload: handleReferralClick,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleReferralClick]);

  useEffect(() => {
    if (isResultModalOpen) {
      setTimeout(() => {
        pageCtx.modals.startFight.current.dismiss();
      }, 700);
    }
  }, [isResultModalOpen, pageCtx]);

  return (
    <>
      <ChallengerModal
        isOpen
        onClose={onClose}
        icon={
          <svg width='63' height='62' viewBox='0 0 63 62' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <circle cx='31.5' cy='31' r='30.5' fill='#FFE000' stroke='#6B6B6B' />
            <path
              d='M20 40.625V29.0536C20 28.1585 20.3855 27.3 21.0717 26.6671C21.758 26.0342 22.6886 25.6786 23.6591 25.6786H26.2727C26.8501 25.6786 27.3182 26.1103 27.3182 26.6428C27.3182 27.1754 26.8501 27.6071 26.2727 27.6071H23.6591C23.2432 27.6071 22.8443 27.7595 22.5502 28.0308C22.2561 28.302 22.0909 28.6699 22.0909 29.0536V40.625C22.0909 41.0086 22.2561 41.3765 22.5502 41.6478C22.8443 41.919 23.2432 42.0714 23.6591 42.0714H39.3409C39.7568 42.0714 40.1557 41.919 40.4498 41.6478C40.7439 41.3765 40.9091 41.0086 40.9091 40.625V29.0536C40.9091 28.6699 40.7439 28.302 40.4498 28.0308C40.1649 27.768 39.7816 27.6168 39.3798 27.6076L39.3409 27.6071H36.7273C36.1499 27.6071 35.6818 27.1754 35.6818 26.6428C35.6818 26.1103 36.1499 25.6786 36.7273 25.6786H39.3409C40.3114 25.6786 41.242 26.0342 41.9283 26.6671C42.6145 27.3 43 28.1585 43 29.0536V40.625C43 41.5201 42.6145 42.3785 41.9283 43.0115C41.242 43.6444 40.3114 44 39.3409 44H23.6591C22.6886 44 21.758 43.6444 21.0717 43.0115C20.3855 42.3785 20 41.5201 20 40.625ZM30.4545 34.4174V20.2922L27.012 23.4675C26.6037 23.8441 25.9418 23.8441 25.5335 23.4675C25.1252 23.091 25.1252 22.4804 25.5335 22.1039L30.7608 17.2824L30.7801 17.2651C31.1897 16.906 31.8373 16.9117 32.2392 17.2824L37.4665 22.1039L37.4853 22.1217C37.8747 22.4995 37.8684 23.0968 37.4665 23.4675C37.0646 23.8382 36.417 23.844 36.0074 23.4849L35.988 23.4675L32.5455 20.2922V34.4174C32.5455 34.95 32.0774 35.3817 31.5 35.3817C30.9226 35.3817 30.4545 34.95 30.4545 34.4174Z'
              fill='#FF451A'
            />
          </svg>
        }
        heading='Share with Friends'
        boldText='Refer a new friend!'
        subText='To complete the game, you have to share the link with them. You will be notified once they accept it!'
      />
      <IonToast isOpen={showToast} onDidDismiss={() => setShowToast(false)} message={toastMessage} duration={2000} />
      <LinkSharedModal isOpen={isResultModalOpen} resultMsg={modalMsg} />
    </>
  );
});

// Add display name for debugging purposes
ReferFriendModalSummary.displayName = 'referFriendModalSummary';

export default ReferFriendModalSummary;
